import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, CheckCircle2, Pencil, Plus, Power, RefreshCw } from 'lucide-react'
import {
  apiAdminCreateKenoLimit, apiAdminGetKenoLimits, apiAdminGetMasters, apiAdminGetPartnerKenoLimits,
  apiAdminPutPartnerKenoLimits, apiAdminUpdateKenoLimit, ApiRequestError,
  type AdminMaster, type KenoBetLimit,
} from '@/lib/api'
import { useT } from '@/contexts/language'
import { useCan } from '@/hooks/usePermission'
import { KenoLimitPicker, rangeText } from '@/components/keno/KenoLimitPicker'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

/**
 * KENO bet limits: the rows of "CHOOSE YOUR BET LIMIT", and which of them each master's line
 * may offer. Two jobs on one screen because they are one decision — you add a row, then you
 * say who gets it. A master narrows further for its own agents on its own console.
 *
 * Rows are switched off, never deleted: a limit is a thing members and agents were pointed
 * at, and a quiet "ระงับใช้งาน" is reversible where a delete is not.
 */

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; limits: KenoBetLimit[] }

/** A whole number from 1, as the API requires. */
const wholeBaht = (v: string): number | null => (/^\d{1,9}$/.test(v.trim()) && Number(v) >= 1 ? Number(v) : null)

interface Draft { id: string | null; min: string; max: string }

function LimitDialog({ draft, onChange, onClose, onSave, saving, error }: {
  draft: Draft
  onChange: (d: Draft) => void
  onClose: () => void
  onSave: () => void
  saving: boolean
  error: string | null
}) {
  const t = useT().kenoLimits
  const min = wholeBaht(draft.min)
  const max = wholeBaht(draft.max)
  const valid = min !== null && max !== null && max >= min
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{draft.id ? t.editTitle : t.addTitle}</DialogTitle>
          <DialogDescription>{t.dialogHint}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => { e.preventDefault(); if (valid && !saving) onSave() }}
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="keno-limit-min">{t.minLabel}</Label>
              <Input
                id="keno-limit-min" inputMode="numeric" autoFocus className="font-mono tabular-nums"
                value={draft.min} onChange={(e) => onChange({ ...draft, min: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="keno-limit-max">{t.maxLabel}</Label>
              <Input
                id="keno-limit-max" inputMode="numeric" className="font-mono tabular-nums"
                value={draft.max} onChange={(e) => onChange({ ...draft, max: e.target.value })}
              />
            </div>
          </div>
          {draft.min !== '' && draft.max !== '' && !valid && (
            <p className="text-xs text-destructive" role="alert">{t.invalidRange}</p>
          )}
          {error && (
            <p className="flex items-center gap-2 font-mono text-xs text-destructive" role="alert">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />{error}
            </p>
          )}
          <DialogFooter>
            <Button type="submit" disabled={!valid || saving}>{t.save}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AssignToMaster() {
  const t = useT().kenoLimits
  const can = useCan()
  const [masters, setMasters] = useState<AdminMaster[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [masterId, setMasterId] = useState<string>('')

  useEffect(() => {
    apiAdminGetMasters().then(setMasters).catch(() => setFailed(true))
  }, [])

  const load = useCallback(() => apiAdminGetPartnerKenoLimits(masterId), [masterId])
  const save = useCallback((ids: string[]) => apiAdminPutPartnerKenoLimits(masterId, ids), [masterId])

  return (
    <section className="flex flex-col gap-3 border border-border rounded-[var(--radius)] bg-card p-4" aria-labelledby="keno-assign-title">
      <div className="flex flex-col gap-1">
        <h2 id="keno-assign-title" className="text-base font-semibold">{t.assignTitle}</h2>
        <p className="text-sm text-muted-foreground max-w-prose">{t.assignHint}</p>
      </div>

      {masters === null && !failed && <Skeleton className="h-9 w-full max-w-xs" />}
      {failed && (
        <p className="flex items-center gap-2 font-mono text-xs text-destructive" role="alert">
          <AlertCircle className="h-4 w-4" aria-hidden />{t.loadFailed}
        </p>
      )}
      {masters !== null && masters.length === 0 && (
        <p className="font-mono text-sm text-muted-foreground">{t.noMasters}</p>
      )}
      {masters !== null && masters.length > 0 && (
        <div className="flex flex-col gap-4 max-w-md">
          <Select value={masterId} onValueChange={setMasterId}>
            <SelectTrigger aria-label={t.pickMaster}><SelectValue placeholder={t.pickMaster} /></SelectTrigger>
            <SelectContent>
              {masters.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name} · {m.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {masterId && <KenoLimitPicker key={masterId} load={load} save={save} canEdit={can('keno-limits:edit')} />}
        </div>
      )}
    </section>
  )
}

export default function AdminKenoLimitsPage() {
  const t = useT().kenoLimits
  const can = useCan()
  const canEdit = can('keno-limits:edit')

  const [state, setState] = useState<LoadState>({ status: 'loading' })
  const [notice, setNotice] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setState({ status: 'loading' })
    try {
      setState({ status: 'ok', limits: await apiAdminGetKenoLimits() })
    } catch (err) {
      setState({ status: 'error', message: err instanceof ApiRequestError ? err.message : t.loadFailed })
    }
  }, [t.loadFailed])

  useEffect(() => { void load() }, [load])

  const reloadQuietly = async () => {
    try { setState({ status: 'ok', limits: await apiAdminGetKenoLimits() }) } catch { void load() }
  }

  const saveDraft = async () => {
    if (!draft) return
    const min = wholeBaht(draft.min)
    const max = wholeBaht(draft.max)
    if (min === null || max === null || max < min) return
    setSaving(true)
    setDialogError(null)
    try {
      if (draft.id) await apiAdminUpdateKenoLimit(draft.id, { min, max })
      else await apiAdminCreateKenoLimit({ min, max })
      setDraft(null)
      setNotice(t.savedLimit)
      await reloadQuietly()
    } catch (err) {
      setDialogError(err instanceof ApiRequestError ? err.message : t.saveFailed)
    } finally {
      setSaving(false)
    }
  }

  const setActive = async (l: KenoBetLimit, isActive: boolean) => {
    setActionError(null)
    setNotice(null)
    try {
      await apiAdminUpdateKenoLimit(l.id, { isActive })
      setNotice(isActive ? t.reactivatedLimit : t.suspendedLimit)
      await reloadQuietly()
    } catch (err) {
      setActionError(err instanceof ApiRequestError ? err.message : t.saveFailed)
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4 max-w-4xl mx-auto">
      <header className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold tracking-tight">{t.title}</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost" size="sm" className="h-8 gap-1.5 font-mono text-xs"
              onClick={() => void load()} disabled={state.status === 'loading'}
            >
              <RefreshCw className={'h-3.5 w-3.5' + (state.status === 'loading' ? ' animate-spin' : '')} aria-hidden />
              {t.reload}
            </Button>
            {canEdit && (
              <Button size="sm" className="h-8 gap-1.5" onClick={() => { setNotice(null); setDialogError(null); setDraft({ id: null, min: '', max: '' }) }}>
                <Plus className="h-3.5 w-3.5" aria-hidden />{t.add}
              </Button>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground max-w-prose">{t.subtitle}</p>
      </header>

      {notice && (
        <div className="flex items-center gap-2 rounded-[var(--radius)] border border-success/40 bg-card p-3" role="status">
          <CheckCircle2 className="h-4 w-4 text-success shrink-0" aria-hidden />
          <p className="text-sm">{notice}</p>
        </div>
      )}
      {actionError && (
        <div className="flex items-center gap-2 rounded-[var(--radius)] border border-destructive/40 bg-card p-3" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" aria-hidden />
          <p className="font-mono text-xs text-destructive">{actionError}</p>
        </div>
      )}

      <section className="flex flex-col gap-2" aria-labelledby="keno-house-title">
        <h2 id="keno-house-title" className="text-base font-semibold">{t.houseTitle}</h2>
        <p className="text-xs text-muted-foreground max-w-prose">{t.houseHint}</p>

        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden">
          {state.status === 'loading' && (
            <div className="flex flex-col gap-3 p-4" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-8 w-24 ml-auto" />
                </div>
              ))}
            </div>
          )}

          {state.status === 'error' && (
            <div className="flex items-center gap-3 p-4" role="alert">
              <AlertCircle className="h-4 w-4 text-destructive shrink-0" aria-hidden />
              <p className="font-mono text-xs text-destructive">{state.message}</p>
              <Button variant="outline" size="sm" className="ml-auto h-8 font-mono text-xs" onClick={() => void load()}>
                {t.reload}
              </Button>
            </div>
          )}

          {state.status === 'ok' && state.limits.length === 0 && (
            <div className="py-12 px-4 text-center">
              <p className="font-mono text-sm text-muted-foreground">{t.empty}</p>
            </div>
          )}

          {state.status === 'ok' && state.limits.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.colRange}</TableHead>
                  <TableHead>{t.colStatus}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.limits.map((l) => (
                  <TableRow key={l.id} className={l.isActive ? undefined : 'text-muted-foreground'}>
                    <TableCell className="font-mono text-sm tabular-nums whitespace-nowrap">{rangeText(l)}</TableCell>
                    <TableCell>
                      <Badge variant={l.isActive ? 'success' : 'outline'} className="whitespace-nowrap">{l.isActive ? t.statusActive : t.statusOff}</Badge>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {canEdit && (
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="outline" size="sm" className="h-11 sm:h-8 gap-1.5 font-mono text-xs"
                            onClick={() => { setNotice(null); setDialogError(null); setDraft({ id: l.id, min: String(l.min), max: String(l.max) }) }}
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden /><span className="sr-only sm:not-sr-only">{t.edit}</span>
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-11 sm:h-8 gap-1.5 font-mono text-xs"
                            onClick={() => void setActive(l, !l.isActive)}
                          >
                            <Power className="h-3.5 w-3.5" aria-hidden /><span className="sr-only sm:not-sr-only">{l.isActive ? t.suspend : t.reactivate}</span>
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>

      <AssignToMaster />

      {draft && (
        <LimitDialog
          draft={draft} onChange={setDraft} onClose={() => setDraft(null)}
          onSave={() => void saveDraft()} saving={saving} error={dialogError}
        />
      )}
    </div>
  )
}
